import React, { useState, useEffect } from 'react';
import {
  EDUCATION_SYSTEMS,
  getGradeLevelsBySystem,
  isLegacyGradeLevel,
  convertLegacyGradeLevel,
  getEducationSystemFromGradeLevel,
  getEducationalStage
} from '../utils/gradeLevels';

export default function GradeLevelSelector({
  value,
  onChange,
  className = '',
  showSystemSelector = true,
  defaultSystem = 'MIXED'
}) {
  const [selectedSystem, setSelectedSystem] = useState(defaultSystem);

  // Initialize system based on current grade level
  useEffect(() => {
    if (value && !isLegacyGradeLevel(value)) {
      const detectedSystem = getEducationSystemFromGradeLevel(value);
      if (detectedSystem && detectedSystem !== selectedSystem) {
        setSelectedSystem(detectedSystem);
      }
    }
  }, [value]);

  // Handle system change
  const handleSystemChange = (newSystem) => {
    setSelectedSystem(newSystem);

    // If current value is not compatible with new system, reset to first option
    const newGradeLevels = getGradeLevelsBySystem(newSystem);
    if (value && !newGradeLevels.find(level => level.value === value)) {
      const firstLevel = newGradeLevels[0];
      if (firstLevel && onChange) {
        onChange(firstLevel.value);
      }
    }
  };

  // Handle grade level change
  const handleGradeLevelChange = (newGradeLevel) => {
    // Convert legacy grade levels if needed
    const convertedGradeLevel = isLegacyGradeLevel(newGradeLevel)
      ? convertLegacyGradeLevel(newGradeLevel)
      : newGradeLevel;

    if (onChange) {
      onChange(convertedGradeLevel);
    }
  };

  const gradeLevels = getGradeLevelsBySystem(selectedSystem);

  return (
    <div className="space-y-3">
      {showSystemSelector && (
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            🌍 Education System
          </label>
          <select
            value={selectedSystem}
            onChange={(e) => handleSystemChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900"
          >
            <option value="MIXED">🌐 All Systems</option>
            <option value="US">🇺🇸 American System</option>
            <option value="UK">🇬🇧 British System</option>
            <option value="INTERNATIONAL">🏫 International System</option>
          </select>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-900 mb-2">
          🎓 Grade Level
        </label>
        <select
          value={value || ''}
          onChange={(e) => handleGradeLevelChange(e.target.value)}
          className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 ${className}`}
        >
          <option value="">Select grade level...</option>
          {(() => {
            // Group levels by educational stage for better organization
            const groupedLevels = gradeLevels.reduce((acc, level) => {
              const stage = getEducationalStage(level.value);
              const stageKey = stage.label;

              if (!acc[stageKey]) {
                acc[stageKey] = {
                  stage: stage,
                  levels: []
                };
              }
              acc[stageKey].levels.push(level);
              return acc;
            }, {});

            return Object.entries(groupedLevels).map(([stageKey, group]) => (
              <optgroup key={stageKey} label={`${group.stage.emoji} ${group.stage.label}`}>
                {group.levels.map((level) => (
                  <option key={level.value} value={level.value}>
                    {level.label}
                  </option>
                ))}
              </optgroup>
            ));
          })()}
        </select>

        {/* Educational stage indicator */}
        {value && (
          <div className="mt-2 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                {selectedSystem === 'US' ? '🇺🇸' : selectedSystem === 'UK' ? '🇬🇧' : selectedSystem === 'INTERNATIONAL' ? '🏫' : '🌐'}
                {EDUCATION_SYSTEMS[selectedSystem] || 'Mixed Systems'}
              </span>

              {(() => {
                const stage = getEducationalStage(value);
                return (
                  <span className="inline-flex items-center px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium">
                    {stage.emoji} {stage.label}
                  </span>
                );
              })()}
            </div>

            {/* Age-appropriate learning characteristics */}
            {(() => {
              const stage = getEducationalStage(value);
              const characteristics = {
                'Early Years': 'Focus on play-based learning, basic concepts, and social skills',
                'Elementary': 'Building foundational literacy, numeracy, and critical thinking',
                'Lower Secondary/Middle School': 'Developing analytical skills and subject specialization',
                'Upper Secondary/High School': 'Advanced concepts, exam preparation, and career planning',
                'Higher Education': 'Independent research, specialization, and professional skills'
              };

              const characteristic = characteristics[stage.label];
              if (characteristic) {
                return (
                  <div className="text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-lg border">
                    💡 <strong>Learning Focus:</strong> {characteristic}
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}
      </div>

      {/* Quick filters for common selections */}
      {selectedSystem === 'MIXED' && (
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 p-4 rounded-lg border border-gray-200">
          <div className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
            ⚡ Quick Select Popular Grades:
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => handleGradeLevelChange('Year 6 (Age 10-11)')}
              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-medium hover:bg-blue-50 hover:border-blue-300 transition-colors text-center"
            >
              <div className="text-lg mb-1">🇬🇧</div>
              <div>Year 6</div>
              <div className="text-gray-500">(Age 10-11)</div>
            </button>
            <button
              type="button"
              onClick={() => handleGradeLevelChange('Year 7 (Age 11-12)')}
              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-medium hover:bg-blue-50 hover:border-blue-300 transition-colors text-center"
            >
              <div className="text-lg mb-1">🇬🇧</div>
              <div>Year 7</div>
              <div className="text-gray-500">(Age 11-12)</div>
            </button>
            <button
              type="button"
              onClick={() => handleGradeLevelChange('Grade 6 (Age 11)')}
              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-medium hover:bg-blue-50 hover:border-blue-300 transition-colors text-center"
            >
              <div className="text-lg mb-1">🇺🇸</div>
              <div>Grade 6</div>
              <div className="text-gray-500">(Age 11)</div>
            </button>
            <button
              type="button"
              onClick={() => handleGradeLevelChange('Grade 9 (Age 14)')}
              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-medium hover:bg-blue-50 hover:border-blue-300 transition-colors text-center"
            >
              <div className="text-lg mb-1">🇺🇸</div>
              <div>Grade 9</div>
              <div className="text-gray-500">(Age 14)</div>
            </button>
            <button
              type="button"
              onClick={() => handleGradeLevelChange('Year 11 (Age 15-16)')}
              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-medium hover:bg-blue-50 hover:border-blue-300 transition-colors text-center"
            >
              <div className="text-lg mb-1">🇬🇧</div>
              <div>Year 11</div>
              <div className="text-gray-500">(Age 15-16)</div>
            </button>
            <button
              type="button"
              onClick={() => handleGradeLevelChange('Grade 12 (Age 17)')}
              className="px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-medium hover:bg-blue-50 hover:border-blue-300 transition-colors text-center"
            >
              <div className="text-lg mb-1">🇺🇸</div>
              <div>Grade 12</div>
              <div className="text-gray-500">(Age 17)</div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}